import { DateTime } from "luxon";

import i18n from "../../i18n";
import type { Warning } from "../../i18n/serverMessages";

export enum PatternStatus {
  Designed = 0,
  Drilling = 1,
  Drilled = 2,
  Charging = 3,
  Charged = 4,
  Fired = 5,
  Closed = 6,
  Cancelled = 7,
}

export const PATTERN_STEPS = [
  PatternStatus.Designed,
  PatternStatus.Drilling,
  PatternStatus.Drilled,
  PatternStatus.Charging,
  PatternStatus.Charged,
  PatternStatus.Fired,
  PatternStatus.Closed,
];

export const PATTERN_STATUS_LABELS: Record<PatternStatus, string> = {
  get [PatternStatus.Designed]() { return i18n.t("patternStatus.designed", { ns: "blastPattern" }); },
  get [PatternStatus.Drilling]() { return i18n.t("patternStatus.drilling", { ns: "blastPattern" }); },
  get [PatternStatus.Drilled]() { return i18n.t("patternStatus.drilled", { ns: "blastPattern" }); },
  get [PatternStatus.Charging]() { return i18n.t("patternStatus.charging", { ns: "blastPattern" }); },
  get [PatternStatus.Charged]() { return i18n.t("patternStatus.charged", { ns: "blastPattern" }); },
  get [PatternStatus.Fired]() { return i18n.t("patternStatus.fired", { ns: "blastPattern" }); },
  get [PatternStatus.Closed]() { return i18n.t("patternStatus.closed", { ns: "blastPattern" }); },
  get [PatternStatus.Cancelled]() { return i18n.t("patternStatus.cancelled", { ns: "blastPattern" }); },
};

export enum PatternShape {
  Square = 0,
  Staggered = 1,
}

export const PATTERN_SHAPE_LABELS: Record<PatternShape, string> = {
  get [PatternShape.Square]() { return i18n.t("patternShape.square", { ns: "blastPattern" }); },
  get [PatternShape.Staggered]() { return i18n.t("patternShape.staggered", { ns: "blastPattern" }); },
};

export enum InitiationSystem {
  Electronic = 0,
  NonElectric = 1,
  Electric = 2,
  Mixed = 3,
}

export const INITIATION_LABELS: Record<InitiationSystem, string> = {
  get [InitiationSystem.Electronic]() { return i18n.t("initiation.electronic", { ns: "blastPattern" }); },
  get [InitiationSystem.NonElectric]() { return i18n.t("initiation.nonElectric", { ns: "blastPattern" }); },
  get [InitiationSystem.Electric]() { return i18n.t("initiation.electric", { ns: "blastPattern" }); },
  get [InitiationSystem.Mixed]() { return i18n.t("initiation.mixed", { ns: "blastPattern" }); },
};

export enum PatternMoveKind {
  Forward = 0,
  Back = 1,
  Cancel = 2,
  Reopen = 3,
}

export interface PatternDesignDTO {
  benchRl?: number | null;
  benchHeightM?: number | null;
  burdenM?: number | null;
  spacingM?: number | null;
  subdrillM?: number | null;
  stemmingM?: number | null;
  stemmingMaterial?: string | null;
  patternShape?: PatternShape | null;
  designBitSizeId?: number | null;
  designHoleCount?: number | null;
  designMetres?: number | null;
  freeFaces?: number | null;
  designExplosiveKg?: number | null;
  designPowderFactor?: number | null;
  designMicKg?: number | null;
  initiationSystem?: InitiationSystem | null;
  rockDensity?: number | null;
  materialDescription?: string | null;
  plannedFireAt?: DateTime | null;
  rowVersion?: string | null;
}

export interface PatternProgressDTO {
  holesDrilled: number;
  metresDrilled: number;
  redrillMetres: number;
  drillingShifts: number;
  firstDrilled?: DateTime | null;
  lastDrilled?: DateTime | null;
  openMisfires: number;
}

export interface PatternDerivedDTO {
  designVolumeBcm?: number | null;
  designPowderFactor?: number | null;
  designKgPerTonne?: number | null;
}

export interface PatternMoveOptionDTO {
  to: PatternStatus;
  label: string;
  kind: PatternMoveKind;
}

export interface PatternDetailDTO {
  id: number;
  reference: string;
  pitName?: string | null;
  pitStage?: string | null;
  blastTypeName?: string | null;
  status: PatternStatus;
  blastDate?: DateTime | null;
  closedAt?: DateTime | null;
  designSource: number;
  designFileName?: string | null;
  bcm: number;
  tonnes: number;
  canEdit: boolean;
  design: PatternDesignDTO;
  derived: PatternDerivedDTO;
  progress: PatternProgressDTO;
  moves: PatternMoveOptionDTO[];
}

export interface PatternMoveRequest {
  to: PatternStatus;
  blastDate?: string | null;
  acknowledge: boolean;
  rowVersion?: string | null;
}

export interface PatternMoveResultDTO {
  moved: boolean;
  warnings: Warning[];
  pattern: PatternDetailDTO;
}

export interface PatternDiggingExcavatorDTO {
  plantId: number;
  plantRef: string;
  shifts: number;
  loads: number;
  tonnes: number;
  lastDug: DateTime;
}

export interface PatternDiggingShiftDTO {
  date: DateTime;
  shift: string;
  plantId: number;
  plantRef: string;
  loads: number;
  tonnes: number;
}

export interface PatternDiggingDTO {
  patternBlastId: number;
  blastedTonnes: number;
  dugTonnes: number;
  remainingTonnes?: number | null;
  firstDug?: DateTime | null;
  lastDug?: DateTime | null;
  openMisfires: number;
  excavators: PatternDiggingExcavatorDTO[];
  shifts: PatternDiggingShiftDTO[];
}
